import { useEffect, useState } from 'react';

interface SearchBarProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  debounceMs?: number;
}

export function SearchBar({
  value,
  onChange,
  placeholder = 'Search conversations',
  debounceMs = 300,
}: SearchBarProps) {
  const [local, setLocal] = useState<string>(value);

  // Keep local in sync if the source-of-truth value changes externally (e.g. URL).
  useEffect(() => {
    setLocal(value);
  }, [value]);

  // Debounce outbound changes.
  useEffect(() => {
    if (local === value) return;
    const t = window.setTimeout(() => {
      onChange(local);
    }, debounceMs);
    return () => window.clearTimeout(t);
  }, [local, value, debounceMs, onChange]);

  return (
    <div
      className="sticky top-0 z-[5] bg-ink-50 px-4 pb-2 dark:bg-[#0B1220]"
      style={{
        // Sticky at the very top of the viewport. We own the safe-area
        // inset HERE so the scroll container above us doesn't add padding
        // (which would let rows scroll up into a gap above us). With
        // safe-area baked into our opaque background, rows are properly
        // hidden when they pass behind us.
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
      }}
    >
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 dark:text-[#64748B]">
          <svg
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        <input
          type="search"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder={placeholder}
          className="block h-11 w-full appearance-none rounded-[10px] border-0 bg-ink-100 pl-9 pr-3 text-[15px] text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-ss-blue/40 dark:bg-[#1E293B] dark:text-[#E2E8F0] dark:placeholder:text-[#64748B]"
          aria-label={placeholder}
        />
      </div>
    </div>
  );
}
